import {
  EXERCISE_DETAIL_TABS,
  type AdminExerciseDetail,
  type ExerciseDetailTab,
} from '@steado/admin-contract/exercises';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { Suspense, lazy, useCallback, useId, useState } from 'react';

import { BadgeCell } from '@/components/table/cells';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';
import { TabSkeleton } from '@/components/panel/panels';
import {
  deactivateExercise,
  deleteExercise,
  exerciseQueryOptions,
  reactivateExercise,
} from '@/screens/exercises/exercises-query';

import { HistoryTab } from '@/screens/catalog-history/HistoryTab';

import { AlternativesTab } from './AlternativesTab';
import { MediaTab } from './MediaTab';
import { OverviewTab } from './OverviewTab';
import type { ExerciseDetailSearch } from './exercise-detail-search';

/**
 * Split off so the body geometry is its own chunk. It is ~100 KB of path data
 * that nine of the console's ten screens never draw, and the entry bundle
 * should not carry it to show a jobs queue. Wrapped rather than default
 * exported, because nothing else in this app has a default export.
 */
const MusclesTab = lazy(() => import('./MusclesTab').then((m) => ({ default: m.MusclesTab })));

type OpenDialog = 'none' | 'deactivate' | 'delete';

/**
 * Deactivate and Delete are outlined rather than solid `destructive`: they sit
 * in the page header beside a read-only summary, and two filled red buttons up
 * there read as a warning about the row itself. The colour is on the label.
 */
const DESTRUCTIVE_OUTLINE = 'border-err-fg/25 text-err-fg hover:bg-err-bg hover:text-err-fg';

const TAB_LABELS: Readonly<Record<ExerciseDetailTab, string>> = {
  overview: 'Overview',
  muscles: 'Muscles',
  alternatives: 'Alternatives',
  media: 'Media',
  history: 'History',
};

export interface ExerciseDetailScreenProps {
  readonly exerciseId: string;
  readonly search: ExerciseDetailSearch;
  readonly onSearchChange: (next: ExerciseDetailSearch) => void;
}

/**
 * One exercise, on its own page.
 *
 * This used to be a 520px drawer over the list. It stopped fitting: the muscle
 * map wants two figures side by side, and stacking that under the clip, the
 * stills and the cues made a column nobody could read. Tabs put each question
 * — what is this, does it train what it claims, is it filmed — on its own
 * surface, and the URL says which one you are looking at.
 */
export function ExerciseDetailScreen({ exerciseId, search, onSearchChange }: ExerciseDetailScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery(exerciseQueryOptions(api, exerciseId));

  const [dialog, setDialog] = useState<OpenDialog>('none');
  const [reason, setReason] = useState('');
  const [isDirty, setDirty] = useState(false);
  const reasonId = useId();

  /**
   * Unsaved edits survive a stray click on the back link.
   *
   * `withResolver` so the prompt is this console's own modal rather than the
   * browser's — `enableBeforeUnload` still covers closing the tab, where a
   * native dialog is the only thing available.
   */
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  const onMutated = useCallback(
    (exercise: AdminExerciseDetail) => {
      queryClient.setQueryData(['admin', 'exercise', exercise.id], exercise);
      // A clip appearing, a name changing or a row retiring all change what the
      // list shows.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exercises'] });
      setDialog('none');
      setReason('');
    },
    [queryClient],
  );

  const deactivate = useMutation({
    mutationFn: () => deactivateExercise(api, exerciseId, reason),
    onSuccess: onMutated,
    retry: false,
  });

  const reactivate = useMutation({
    mutationFn: () => reactivateExercise(api, exerciseId),
    onSuccess: onMutated,
    retry: false,
  });

  const remove = useMutation({
    mutationFn: () => deleteExercise(api, exerciseId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['admin', 'exercise', exerciseId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exercises'] });
      // Back to the library: there is no page left to stand on.
      void navigate({ to: '/exercises', replace: true });
    },
    retry: false,
  });

  const onSelectTab = useCallback(
    (tab: ExerciseDetailTab) => {
      onSearchChange({ tab });
    },
    [onSearchChange],
  );

  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const exercise = query.data;
  const isBusy = deactivate.isPending || reactivate.isPending || remove.isPending;
  const disabledTitle = can.mutate ? undefined : 'Changing the library requires the admin role.';

  // Deleting is offered only while nothing points at the row. Everything else
  // is what `deactivate` is for, and the server refuses anyway. Ternary rather
  // than a `&&` chain for the same reason as `isUnreviewed` below.
  const isUnused =
    exercise === undefined ? false : exercise.usage.setLogs === 0 && exercise.usage.plannedItems === 0;

  // Written as a ternary rather than a `&&` chain: an optional-chain autofix
  // over `exercise !== undefined && exercise.sourceId !== null` would collapse
  // to `exercise?.sourceId !== null`, which is true while the row is loading.
  const isUnreviewed = exercise === undefined ? false : exercise.sourceId !== null && !exercise.isActive;

  return (
    <div className="flex flex-col gap-3.5">
      {exercise === undefined ? (
        <TabSkeleton rows={2} />
      ) : (
        <section
          aria-label="Exercise summary"
          className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-border bg-card px-5 py-4"
        >
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[17px] font-semibold text-foreground">{exercise.name}</h2>
              <BadgeCell label={exercise.category} tone="mute" />
              <BadgeCell
                label={exercise.isWeighted ? 'weighted' : 'bodyweight'}
                tone={exercise.isWeighted ? 'info' : 'mute'}
              />
              <BadgeCell label={exercise.difficulty} tone="mute" />
              {exercise.isActive ? null : <BadgeCell label="deactivated" tone="err" dot />}
              {exercise.sourceId === null ? null : <BadgeCell label="imported" tone="warn" />}
            </div>
            <p className="font-mono text-[13px] text-muted-foreground">{exercise.slug}</p>
          </div>

          <div className="flex items-center gap-3">
            {isUnused ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialog('delete');
                }}
                disabled={!can.mutate || isBusy}
                title={disabledTitle}
                className={DESTRUCTIVE_OUTLINE}
              >
                Delete…
              </Button>
            ) : null}

            {exercise.isActive ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialog('deactivate');
                }}
                disabled={!can.mutate || isBusy}
                title={disabledTitle}
                className={DESTRUCTIVE_OUTLINE}
              >
                Deactivate…
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reactivate.mutate();
                }}
                disabled={!can.mutate || isBusy}
                title={disabledTitle}
              >
                Reactivate
              </Button>
            )}
          </div>
        </section>
      )}

      {isUnreviewed ? (
        <Alert tone="warn">
          <AlertDescription>
            Imported and not yet reviewed. Nobody has checked the muscle map, the equipment or the
            contraindications on this row, and it reaches no user&apos;s plan until it is activated. Read them
            before you activate it.
          </AlertDescription>
        </Alert>
      ) : null}

      {reactivate.isError ? (
        <p role="alert" className="text-[13px] text-err-fg">
          {reactivate.error.message}
        </p>
      ) : null}

      <div className="flex flex-col gap-4">
        {/*
          Hand-rolled rather than the shared Tabs component: the open tab is a
          search param, and these ids are what the tabpanel below points its
          `aria-labelledby` at.
        */}
        <div
          role="tablist"
          aria-label="Exercise detail"
          className="flex items-center gap-1 border-b border-border"
        >
          {EXERCISE_DETAIL_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`tab-${tab}`}
              aria-selected={search.tab === tab}
              aria-controls={`tabpanel-${tab}`}
              onClick={() => {
                onSelectTab(tab);
              }}
              className={cn(
                '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                search.tab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        <div role="tabpanel" id={`tabpanel-${search.tab}`} aria-labelledby={`tab-${search.tab}`}>
          {/*
            Every tab reads the one detail query, so unmounting the closed ones
            costs no request — it only keeps the muscle map's chunk unfetched
            until someone asks for it.
          */}
          {exercise === undefined ? <TabSkeleton rows={5} /> : null}
          {exercise !== undefined && search.tab === 'overview' ? (
            <OverviewTab exercise={exercise} onSaved={onMutated} onDirtyChange={setDirty} />
          ) : null}
          {exercise !== undefined && search.tab === 'muscles' ? (
            <Suspense fallback={<TabSkeleton rows={3} />}>
              <MusclesTab exercise={exercise} />
            </Suspense>
          ) : null}
          {exercise !== undefined && search.tab === 'alternatives' ? (
            <AlternativesTab exercise={exercise} onMutated={onMutated} />
          ) : null}
          {exercise !== undefined && search.tab === 'media' ? (
            <MediaTab exercise={exercise} onMutated={onMutated} />
          ) : null}
          {exercise !== undefined && search.tab === 'history' ? (
            <HistoryTab
              subject="exercise"
              slug={exercise.slug}
              name={exercise.name}
              currentNames={[]}
              addedTitle={exercise.sourceId === null ? 'Added in the console' : 'Imported to the library'}
              onOpenEvent={(key) => {
                void navigate({ to: '/catalog-changes', search: { window: 'all', event: key } });
              }}
            />
          ) : null}
        </div>
      </div>

      {dialog === 'deactivate' && exercise !== undefined ? (
        <ConfirmDialog
          title={`Deactivate ${exercise.name}?`}
          body={
            <div className="flex flex-col gap-3">
              <p>
                It stops being offered to plan generation.{' '}
                <span className="font-mono">{String(exercise.usage.setLogs)}</span> logged set
                {exercise.usage.setLogs === 1 ? '' : 's'} keep pointing at it, and nothing is deleted.
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor={reasonId} className="text-xs font-medium text-muted-foreground">
                  Reason (recorded in the audit log)
                </Label>
                <Input
                  id={reasonId}
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </div>
            </div>
          }
          confirmLabel="Deactivate"
          destructive
          isPending={deactivate.isPending}
          {...(deactivate.isError ? { error: deactivate.error.message } : {})}
          onConfirm={() => {
            deactivate.mutate();
          }}
          onCancel={() => {
            setDialog('none');
            setReason('');
          }}
        />
      ) : null}

      {dialog === 'delete' && exercise !== undefined ? (
        <ConfirmDialog
          title={`Delete ${exercise.name}?`}
          body={
            <div className="flex flex-col gap-3">
              <p>
                The row, its demo clip and its {exercise.images.length} still
                {exercise.images.length === 1 ? '' : 's'} are removed and do not come back. This is offered
                only because nothing references this movement: no logged set, no planned slot. The audit entry
                survives it.
              </p>
              <p className="text-muted-foreground">
                If you only want it out of planning, cancel and deactivate instead: that is reversible and
                keeps the row.
              </p>
            </div>
          }
          confirmLabel="Delete permanently"
          destructive
          // The one action in this screen with no undo, so it gets the speed
          // bump the design reserves for exactly that.
          typeToConfirm={exercise.name}
          isPending={remove.isPending}
          {...(remove.isError ? { error: remove.error.message } : {})}
          onConfirm={() => {
            remove.mutate();
          }}
          onCancel={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {blocker.status === 'blocked' ? (
        <ConfirmDialog
          title="Leave without saving?"
          body={
            <p>
              This exercise has edits that have not been saved. Leaving now discards them. The row keeps what
              it had.
            </p>
          }
          confirmLabel="Discard and leave"
          destructive
          onConfirm={() => {
            setDirty(false);
            blocker.proceed();
          }}
          onCancel={() => {
            blocker.reset();
          }}
        />
      ) : null}
    </div>
  );
}
